import React, { useState, useContext, useEffect } from 'react';
import { doc, updateDoc, getDoc, collection, getDocs, query, setDoc, where, orderBy, limit, deleteDoc, increment, addDoc } from 'firebase/firestore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSearch, 
  faPlus, 
  faTimes, 
  faTrash,
  faUserPlus,
  faSpinner,
  faUsers,
  faEdit,
  faLock,
  faUser,
  faCheck
} from '@fortawesome/free-solid-svg-icons';
import { liteClient } from 'algoliasearch/lite';
import { UserContext } from '../../context/UserContext';
import { getFirestoreInstance } from '../../utils/firebaseHelper';
import { createNotification } from '../../utils/NotificationService';
import verifiedBadge from '../../assets/Images/profileverifiedtick.png';
import defaultUserImage from '../../assets/Images/defaultUserProfilePicture.png';
import LeftMenu from '../LeftMenu';
import RightMenu from '../RightMenu';
import styles from './Staff.module.css';
import AuthCheckWrapper from '../AuthCheckWrapper';

// Import useNavigate for profile navigation
import { useNavigate } from 'react-router-dom';

// Initialize Algolia
const client = liteClient("6DJNAZSX4P", "342932cbe26f7751ec87fa2b1ff32e4c");

// Permission Denied Component
const PermissionDenied = () => (
  <div className={styles.permissionDenied}>
    <FontAwesomeIcon icon={faLock} className={styles.lockIcon} />
    <h2>Access Restricted</h2>
    <p>You don't have permission to view this page. This section is only available to Vendor and Staff accounts.</p>
  </div>
);

const Staff = () => {
  const [isStaffModalVisible, setIsStaffModalVisible] = useState(false);
  const [staffMembers, setStaffMembers] = useState([]);
  const [displayedStaff, setDisplayedStaff] = useState([]);
  const [hasMoreStaff, setHasMoreStaff] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const { userData } = useContext(UserContext);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [tours, setTours] = useState([]); // For RightMenu
  const [staffCount, setStaffCount] = useState(0); // Total count for staff
  // Add navigate hook for routing
  const navigate = useNavigate();
  const [hasAccess, setHasAccess] = useState(false);
  
  // Staff search states
  const [staffSearchQuery, setStaffSearchQuery] = useState('');
  const [staffResults, setStaffResults] = useState([]);
  const [searchingStaff, setSearchingStaff] = useState(false);

  // Replace single access level with individual permissions
  const [permissions, setPermissions] = useState({
    canAddItems: false,
    canEditItems: false,
    canDeleteItems: false,
    canChangeStatus: false
  });

  const [editingStaff, setEditingStaff] = useState(null);
  const [editPermissions, setEditPermissions] = useState({
    canAddItems: false,
    canEditItems: false,
    canDeleteItems: false,
    canChangeStatus: false
  });
  const [showEditPermissionsModal, setShowEditPermissionsModal] = useState(false);

  // Check if user has access based on userType
  useEffect(() => {
    if (userData) {
      setHasAccess(userData.userType === 'Vendor' || userData.userType === 'Staff');
    }
  }, [userData]);

  // Check for screen size changes
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Load staff details with subcollection pattern
  useEffect(() => {
    if (!userData?.userId) return;
    
    const loadStaffData = async () => {
      setIsProcessing(true);
      try {
        const db = getFirestoreInstance();
        
        // First get the user document to get the staff count and top 3 staff
        const userDoc = await getDoc(doc(db, 'users', userData.userId));
        let initialStaff = [];
        let totalStaffCount = 0;
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          initialStaff = userData.staff || [];
          totalStaffCount = userData.staffCount || 0;
          
          // Set the count state
          setStaffCount(totalStaffCount);
        }
        
        // Set the initial displayed staff
        setDisplayedStaff(initialStaff);
        
        // Check if there are more staff than what's displayed
        setHasMoreStaff(totalStaffCount > initialStaff.length);
        
        // If we need to display more staff, fetch them from subcollection
        if (initialStaff.length > 0) {
          // Fetch all staff from subcollection for complete data
          const staffRef = collection(db, 'users', userData.userId, 'staff');
          const staffQuery = query(
            staffRef,
            orderBy('createdAt', 'desc')
          );
          
          const staffSnapshot = await getDocs(staffQuery);
          
          if (!staffSnapshot.empty) {
            const allStaff = await Promise.all(
              staffSnapshot.docs.map(async (docSnapshot) => {
                const staffData = docSnapshot.data();
                
                // Enrich with user data
                if (staffData.userId) {
                  try {
                    const userDoc = await getDoc(doc(db, 'users', staffData.userId));
                    if (userDoc.exists()) {
                      const userData = userDoc.data();
                      return {
                        id: docSnapshot.id,
                        userId: staffData.userId,
                        fullName: userData.fullName || '',
                        userName: userData.userName || '',
                        profilePictureUrl: userData.profilePictureUrl || '',
                        userSubType: userData.userSubType || '',
                        email: userData.email || '',
                        idVerificationStatus: userData.idVerificationStatus || '',
                        status: staffData.status || 'pending',
                        role: staffData.role || 'staff',
                        permissions: staffData.permissions || {},
                        createdAt: staffData.createdAt || new Date().toISOString()
                      };
                    }
                  } catch (error) {
                    console.error(`Error fetching user data for ${staffData.userId}:`, error);
                  }
                }
                
                // Return the staff data as is if user data enrichment failed
                return {
                  id: docSnapshot.id,
                  ...staffData
                };
              })
            );
            
            // Set the full staff list
            setStaffMembers(allStaff);
          } else {
            setStaffMembers(initialStaff);
          }
        }
      } catch (error) {
        console.error('Error fetching staff data:', error);
      } finally {
        setIsProcessing(false);
      }
    };
    
    loadStaffData();
  }, [userData?.userId]);

  // Add function to navigate to staff profile
  const navigateToUserProfile = (userName) => {
    navigate(`/${userName}`);
  };

  const handleStaffSearch = async () => {
    if (!staffSearchQuery.trim()) return;
    
    try {
      setSearchingStaff(true);
      const { results } = await client.search([{
        indexName: 'users_index',
        query: staffSearchQuery,
        filters: 'userType:Staff', // Search for regular users who can be staff
        hitsPerPage: 10
      }]);
  
      const formattedStaff = results[0].hits.map(hit => ({
        id: hit.objectID,
        fullName: hit.fullName,
        email: hit.email,
        userName: hit.userName,
        userSubType: hit.userSubType,
        profilePictureUrl: hit.profilePictureUrl,
        userType: hit.userType,
        idVerificationStatus: hit.idVerificationStatus,
        isAlreadyAdded: staffMembers.some(staff => staff.userId === hit.objectID)
      }));
      
      setStaffResults(formattedStaff);
    } catch (error) {
      console.error('Error searching staff:', error);
      setStaffResults([]);
    } finally {
      setSearchingStaff(false);
    }
  };

  // Load more staff members from subcollection
  const loadMoreStaff = async () => {
    if (isLoadingMore || !userData?.userId) return;
    
    setIsLoadingMore(true);
    try {
      // Skip loading more if we already have all staff loaded
      if (displayedStaff.length >= staffCount) {
        setHasMoreStaff(false);
        return;
      }
      
      // Load the next batch of staff members
      const nextBatch = staffMembers.slice(
        displayedStaff.length,
        displayedStaff.length + 6
      );
      
      setDisplayedStaff(prev => [...prev, ...nextBatch]);
      
      // Check if there are more staff to load
      setHasMoreStaff(displayedStaff.length + nextBatch.length < staffCount);
    } catch (error) {
      console.error('Error loading more staff:', error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const getPermissionBadges = (permissions) => {
    if (!permissions) return <span className={styles.noBadge}>No Permissions</span>;
    
    const badges = [];
    
    if (permissions.canAddItems) {
      badges.push(<span key="add" className={`${styles.permissionBadge} ${styles.addBadge}`}>Add</span>);
    }
    
    if (permissions.canEditItems) {
      badges.push(<span key="edit" className={`${styles.permissionBadge} ${styles.editBadge}`}>Edit</span>);
    }
    
    if (permissions.canDeleteItems) {
      badges.push(<span key="delete" className={`${styles.permissionBadge} ${styles.deleteBadge}`}>Delete</span>);
    }
    
    if (permissions.canChangeStatus) {
      badges.push(<span key="status" className={`${styles.permissionBadge} ${styles.statusBadge}`}>Status</span>);
    }
    
    if (badges.length === 0) {
      return <span className={styles.noBadge}>No Permissions</span>;
    }
    
    return <div className={styles.badgeContainer}>{badges}</div>;
  };
  
  const handleAddStaff = async (staff, permissions) => {
    if (!staff || staff.isAlreadyAdded) return;
    
    setIsProcessing(true);
    try {
      const db = getFirestoreInstance();
      
      // Prepare staff data with permissions
      const staffData = {
        userId: staff.id,
        status: 'pending',
        role: 'staff',
        permissions: permissions,
        addedAt: new Date().toISOString(),
        createdAt: new Date().toISOString()
      };
      
      // Add to subcollection
      const staffRef = collection(db, 'users', userData.userId, 'staff');
      const newStaffRef = await addDoc(staffRef, staffData);
      
      // Get enriched staff data for UI
      const enrichedStaffData = {
        id: newStaffRef.id,
        userId: staff.id,
        fullName: staff.fullName || '',
        userName: staff.userName || '',
        profilePictureUrl: staff.profilePictureUrl || '',
        userSubType: staff.userSubType || '',
        email: staff.email || '',
        idVerificationStatus: staff.idVerificationStatus || '',
        status: 'pending',
        role: 'staff',
        permissions: permissions,
        createdAt: new Date().toISOString()
      };
      
      // Update both state arrays
      setStaffMembers(prev => [enrichedStaffData, ...prev]);
      
      // Update the top 3 staff in the main document
      const top3Query = query(
        staffRef,
        orderBy('createdAt', 'desc'),
        limit(3)
      );
      
      const top3Snapshot = await getDocs(top3Query);
      const top3Staff = await Promise.all(
        top3Snapshot.docs.map(async (docSnapshot) => {
          const staffData = docSnapshot.data();
          
          // Enrich with user data
          if (staffData.userId) {
            try {
              const userDoc = await getDoc(doc(db, 'users', staffData.userId));
              if (userDoc.exists()) {
                const userData = userDoc.data();
                return {
                  id: docSnapshot.id,
                  userId: staffData.userId,
                  fullName: userData.fullName || '',
                  userName: userData.userName || '',
                  profilePictureUrl: userData.profilePictureUrl || '',
                  userSubType: userData.userSubType || '',
                  status: staffData.status || 'pending',
                  role: staffData.role || 'staff',
                  permissions: staffData.permissions || {},
                  createdAt: staffData.createdAt
                };
              }
            } catch (error) {
              console.error(`Error fetching user data for ${staffData.userId}:`, error);
            }
          }
          
          return {
            id: docSnapshot.id,
            ...staffData
          };
        })
      );
      
      // Increment the staff count
      const newStaffCount = staffCount + 1;
      setStaffCount(newStaffCount);
      
      // Update main document with top3 and the count
      const userRef = doc(db, 'users', userData.userId);
      await updateDoc(userRef, {
        staff: top3Staff,
        staffCount: newStaffCount
      });
      
      // Update displayed staff
      setDisplayedStaff(top3Staff);
      
      // Update hasMoreStaff based on the new count
      setHasMoreStaff(newStaffCount > top3Staff.length);
      
      // Create staff invitation notification with permissions
      const invitationData = {
        organizationId: userData?.userId || null,
        organizationName: userData?.fullName || '',
        organizationType: userData?.userSubType || 'Vendor',
        userId: staff.id || null,
        userName: staff.userName || '',
        fullName: staff.fullName || '',
        email: staff.email || '',
        profilePictureUrl: staff.profilePictureUrl || '',
        userType: 'User',
        userSubType: staff.userSubType || '',
        role: 'Staff',
        read: false,
        pendingTimestamp: new Date().toISOString(),
        timestamp: new Date().toISOString(),
        status: 'pending',
        type: 'STAFF_REQUEST',
        title: 'Staff Request',
        message: `${userData?.fullName || 'A vendor'} wants to add you as staff. Accept to join their organization.`,
        permissions: permissions, // Include permissions in notification
        createdBy: {
          userId: userData?.userId || null,
          fullName: userData?.fullName || '',
          userName: userData?.userName || '',
          email: userData?.email || '',
          profilePictureUrl: userData?.profilePictureUrl || '',
          userType: userData?.userType || '',
          userSubType: userData?.userSubType || ''
        }
      };
      
      // Create the notification
      await createNotification(staff.id, 'general', invitationData);
      
      // Clear search
      setStaffSearchQuery('');
      setStaffResults([]);
      
      // Close modal
      setIsStaffModalVisible(false);
      
      // Reset permissions
      setPermissions({
        canAddItems: false,
        canEditItems: false,
        canDeleteItems: false,
        canChangeStatus: false
      });
      
    } catch (error) {
      console.error('Error adding staff:', error);
      alert('Failed to add staff. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRemoveStaff = async (staffId) => {
    if (!window.confirm('Are you sure you want to remove this staff member?')) return;
    
    setIsProcessing(true);
    try {
      const db = getFirestoreInstance();
      
      // Find staff to get userId for notification
      const staffToRemove = staffMembers.find(s => s.id === staffId);
      if (!staffToRemove) {
        throw new Error('Staff member not found');
      }
      
      // Remove from vendor's staff subcollection
      const staffRef = doc(db, 'users', userData.userId, 'staff', staffId);
      await deleteDoc(staffRef);
      
      // Update both state arrays
      setStaffMembers(prev => prev.filter(s => s.id !== staffId));
      setDisplayedStaff(prev => prev.filter(s => s.id !== staffId));
      
      // Decrement the staff count
      const newStaffCount = Math.max(0, staffCount - 1);
      setStaffCount(newStaffCount);
      
      // Get new top 3 staff for main document
      const staffColRef = collection(db, 'users', userData.userId, 'staff');
      const top3Query = query(
        staffColRef,
        orderBy('createdAt', 'desc'),
        limit(3)
      );
      
      const top3Snapshot = await getDocs(top3Query);
      const top3Staff = await Promise.all(
        top3Snapshot.docs.map(async (docSnapshot) => {
          const staffData = docSnapshot.data();
          
          // Enrich with user data
          if (staffData.userId) {
            try {
              const userDoc = await getDoc(doc(db, 'users', staffData.userId));
              if (userDoc.exists()) {
                const userData = userDoc.data();
                return {
                  id: docSnapshot.id,
                  userId: staffData.userId,
                  fullName: userData.fullName || '',
                  userName: userData.userName || '',
                  profilePictureUrl: userData.profilePictureUrl || '',
                  userSubType: userData.userSubType || '',
                  status: staffData.status || 'pending',
                  role: staffData.role || 'staff',
                  permissions: staffData.permissions || {},
                  createdAt: staffData.createdAt
                };
              }
            } catch (error) {
              console.error(`Error fetching user data for ${staffData.userId}:`, error);
            }
          }
          
          return {
            id: docSnapshot.id,
            ...staffData
          };
        })
      );
      
      // Update main document with top3 and the count
      const userRef = doc(db, 'users', userData.userId);
      await updateDoc(userRef, {
        staff: top3Staff,
        staffCount: newStaffCount
      });
      
      // Update hasMoreStaff based on the new count
      setHasMoreStaff(newStaffCount > top3Staff.length);
      
      // If the staff has already accepted, we should also update their organization subcollection
      if (staffToRemove.status === 'accepted') {
        try {
          const orgRef = doc(db, 'users', staffToRemove.userId, 'organizations', userData.userId);
          const orgDoc = await getDoc(orgRef);
          
          if (orgDoc.exists()) {
            await updateDoc(orgRef, {
              status: 'removed',
              removedAt: new Date().toISOString()
            });
          }
        } catch (err) {
          console.error('Error updating staff organization entry:', err);
          // Continue even if this fails
        }
        
        // Create notification for the staff member
        const notificationData = {
          organizationId: userData?.userId || null,
          organizationName: userData?.fullName || '',
          type: 'STAFF_REMOVAL',
          title: 'Staff Removal',
          message: `${userData?.fullName || 'A vendor'} has removed you as staff from their organization.`,
          status: 'removed',
          timestamp: new Date().toISOString(),
          createdBy: {
            userId: userData?.userId || null,
            fullName: userData?.fullName || '',
            userSubType: userData?.userSubType || ''
          }
        };
        
        await createNotification(staffToRemove.userId, 'general', notificationData);
      }
      
    } catch (error) {
      console.error('Error removing staff:', error);
      alert('Failed to remove staff. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Add function to handle staff access level edit
  const handleEditStaff = (staff) => {
    setEditingStaff(staff);
    setEditPermissions(staff.permissions || {
      canAddItems: false,
      canEditItems: false,
      canDeleteItems: false,
      canChangeStatus: false
    });
    setShowEditPermissionsModal(true);
  };

  const updateEditPermission = (key, value) => {
    setEditPermissions(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Add function to update staff access level
  const handleUpdateStaffPermissions = async () => {
    if (!editingStaff) return;
    
    setIsProcessing(true);
    try {
      const db = getFirestoreInstance();
      
      // Update in subcollection
      const staffRef = doc(db, 'users', userData.userId, 'staff', editingStaff.id);
      await updateDoc(staffRef, {
        permissions: editPermissions,
        updatedAt: new Date().toISOString()
      });
      
      // Check if this staff is in the top 3
      const userDoc = await getDoc(doc(db, 'users', userData.userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const staffArray = userData.staff || [];
        
        const isInTop3 = staffArray.some(s => s.id === editingStaff.id);
        
        if (isInTop3) {
          // Update the staff member in the top 3
          const updatedStaffArray = staffArray.map(s => 
            s.id === editingStaff.id ? 
              { ...s, permissions: editPermissions } : 
              s
          );
          
          // Update main document
          const userRef = doc(db, 'users', userData.userId);
          await updateDoc(userRef, {
            staff: updatedStaffArray
          });
          
          // Update displayed staff
          setDisplayedStaff(updatedStaffArray);
        }
      }
      
      // Update local staff members array
      setStaffMembers(prev => 
        prev.map(staff => 
          staff.id === editingStaff.id ? 
            { ...staff, permissions: editPermissions } : 
            staff
        )
      );
      
      // If the staff member has accepted, update their organization subcollection as well
      if (editingStaff.status === 'accepted') {
        try {
          const orgRef = doc(db, 'users', editingStaff.userId, 'organizations', userData.userId);
          await updateDoc(orgRef, {
            permissions: editPermissions,
            updatedAt: new Date().toISOString()
          });
        } catch (err) {
          console.error('Error updating staff organization entry:', err);
        }
      }
      
      // Create notification for the staff member
      const notificationData = {
        type: 'PERMISSIONS_UPDATED',
        title: 'Permissions Updated',
        message: `Your inventory permissions have been updated.`,
        organizationId: userData.userId,
        organizationName: userData.fullName || '',
        timestamp: new Date().toISOString(),
        permissions: editPermissions,
        createdBy: {
          userId: userData.userId,
          fullName: userData.fullName || '',
          userName: userData.userName || ''
        }
      };
      
      await createNotification(editingStaff.userId, 'general', notificationData);
      
      setShowEditPermissionsModal(false);
      setEditingStaff(null);
      
    } catch (error) {
      console.error('Error updating staff permissions:', error);
      alert('Failed to update staff permissions. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const EditPermissionsModal = () => {
    if (!showEditPermissionsModal || !editingStaff) return null;
    
    return (
      <div className={styles.modalOverlay} onClick={() => setShowEditPermissionsModal(false)}>
        <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
          <div className={styles.modalHeader}>
            <h2>Edit Staff Permissions</h2>
            <button className={styles.closeButton} onClick={() => setShowEditPermissionsModal(false)}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          
          <div className={styles.staffInfoPreview}>
            <img
              src={editingStaff.profilePictureUrl || defaultUserImage}
              alt={editingStaff.fullName}
              className={styles.staffImagePreview}
            />
            <div>
              <h3>{editingStaff.fullName}</h3>
              <p>@{editingStaff.userName}</p>
            </div>
          </div>
          
          <div className={styles.permissionsSection}>
            <h3 className={styles.sectionTitle}>Inventory Permissions</h3>
            <h5 className={styles.sectionSubTitle}>(Leave unchecked for no permission)</h5>
            <div className={styles.permissionsList}>
              <label className={styles.permissionOption}>
                <input
                  type="checkbox"
                  checked={editPermissions.canAddItems}
                  onChange={(e) => updateEditPermission('canAddItems', e.target.checked)}
                  className={styles.permissionCheckbox}
                />
                <div className={styles.permissionLabel}>
                  <span className={styles.permissionTitle}>Add Items</span>
                  <span className={styles.permissionDescription}>Can add new inventory items</span>
                </div>
              </label>
              
              <label className={styles.permissionOption}>
                <input
                  type="checkbox"
                  checked={editPermissions.canEditItems}
                  onChange={(e) => updateEditPermission('canEditItems', e.target.checked)}
                  className={styles.permissionCheckbox}
                />
                <div className={styles.permissionLabel}>
                  <span className={styles.permissionTitle}>Edit Items</span>
                  <span className={styles.permissionDescription}>Can modify existing inventory items</span>
                </div>
              </label>
              
              <label className={styles.permissionOption}>
                <input
                  type="checkbox"
                  checked={editPermissions.canDeleteItems}
                  onChange={(e) => updateEditPermission('canDeleteItems', e.target.checked)}
                  className={styles.permissionCheckbox}
                />
                <div className={styles.permissionLabel}>
                  <span className={styles.permissionTitle}>Delete Items</span>
                  <span className={styles.permissionDescription}>Can remove inventory items</span>
                </div>
              </label>
              
              <label className={styles.permissionOption}>
                <input
                  type="checkbox"
                  checked={editPermissions.canChangeStatus}
                  onChange={(e) => updateEditPermission('canChangeStatus', e.target.checked)}
                  className={styles.permissionCheckbox}
                />
                <div className={styles.permissionLabel}>
                  <span className={styles.permissionTitle}>Change Status</span>
                  <span className={styles.permissionDescription}>Can mark items as available, damaged, or maintenance</span>
                </div>
              </label>
            </div>
          </div>
          
          <div className={styles.modalActions}>
            <button
              className={styles.cancelButton}
              onClick={() => setShowEditPermissionsModal(false)}
            >
              Cancel
            </button>
            <button
              className={styles.saveButton}
              onClick={handleUpdateStaffPermissions}
              disabled={isProcessing}
            >
              {isProcessing ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Handle tour selection (for RightMenu)
  const handleTourSelect = (tour) => {
    // Navigate to tour or update state as needed
  };

  // Staff Search Modal Component
  const StaffSearchModal = () => {
    if (!isStaffModalVisible) return null;
    
    // Helper function to update a single permission
    const updatePermission = (key, value) => {
      setPermissions(prev => ({
        ...prev,
        [key]: value
      }));
    };
    
    return (
      <div className={styles.modalOverlay} onClick={() => setIsStaffModalVisible(false)}>
        <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
          <div className={styles.modalHeader}>
            <h2>Add Staff</h2>
            <button className={styles.closeButton} onClick={() => setIsStaffModalVisible(false)}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          
          <div className={styles.searchBarContainer}>
            <input
              type="text"
              placeholder="Search staff by name..."
              value={staffSearchQuery}
              onChange={(e) => setStaffSearchQuery(e.target.value)}
              className={styles.searchInput}
            />
            <button
              className={styles.searchButton}
              onClick={handleStaffSearch}
              disabled={!staffSearchQuery.trim() || searchingStaff}
            >
              {searchingStaff ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                <FontAwesomeIcon icon={faSearch} />
              )}
              <span>{searchingStaff ? 'Searching...' : 'Search'}</span>
            </button>
          </div>
          
          {/* Replace access level section with permissions checkboxes */}
          <div className={styles.permissionsSection}>
            <h3 className={styles.sectionTitle}>Inventory Permissions</h3>
            <h5 className={styles.sectionSubTitle}>(Leave unchecked for no permission)</h5>
            <div className={styles.permissionsList}>
              <label className={styles.permissionOption}>
                <input
                  type="checkbox"
                  checked={permissions.canAddItems}
                  onChange={(e) => updatePermission('canAddItems', e.target.checked)}
                  className={styles.permissionCheckbox}
                />
                <div className={styles.permissionLabel}>
                  <span className={styles.permissionTitle}>Add Items</span>
                  <span className={styles.permissionDescription}>Can add new inventory items</span>
                </div>
              </label>
              
              <label className={styles.permissionOption}>
                <input
                  type="checkbox"
                  checked={permissions.canEditItems}
                  onChange={(e) => updatePermission('canEditItems', e.target.checked)}
                  className={styles.permissionCheckbox}
                />
                <div className={styles.permissionLabel}>
                  <span className={styles.permissionTitle}>Edit Items</span>
                  <span className={styles.permissionDescription}>Can modify existing inventory items</span>
                </div>
              </label>
              
              <label className={styles.permissionOption}>
                <input
                  type="checkbox"
                  checked={permissions.canDeleteItems}
                  onChange={(e) => updatePermission('canDeleteItems', e.target.checked)}
                  className={styles.permissionCheckbox}
                />
                <div className={styles.permissionLabel}>
                  <span className={styles.permissionTitle}>Delete Items</span>
                  <span className={styles.permissionDescription}>Can remove inventory items</span>
                </div>
              </label>
              
              <label className={styles.permissionOption}>
                <input
                  type="checkbox"
                  checked={permissions.canChangeStatus}
                  onChange={(e) => updatePermission('canChangeStatus', e.target.checked)}
                  className={styles.permissionCheckbox}
                />
                <div className={styles.permissionLabel}>
                  <span className={styles.permissionTitle}>Change Status</span>
                  <span className={styles.permissionDescription}>Can mark items as available, damaged, or maintenance</span>
                </div>
              </label>
            </div>
          </div>
          
          <div className={styles.searchResults}>
            {searchingStaff ? (
              <div className={styles.noResults}>Searching...</div>
            ) : staffResults.length > 0 ? (
              staffResults.map((staff) => (
                <div
                  key={staff.id}
                  className={`${styles.searchResult} ${staff.isAlreadyAdded ? styles.disabled : ''}`}
                  onClick={() => !staff.isAlreadyAdded && handleAddStaff(staff, permissions)}
                >
                  <img
                    src={staff.profilePictureUrl || defaultUserImage}
                    alt={staff.fullName}
                    className={styles.profilePhoto}
                  />
                  <div className={styles.userInfo}>
                    <div className={styles.nameContainer}>
                      <span className={styles.fullName}>{staff.fullName}</span>
                      {staff.idVerificationStatus === 'Verified' && (
                        <img src={verifiedBadge} alt="Verified" className={styles.verifiedBadge} />
                      )}
                    </div>
                    <span className={styles.userName}>@{staff.userName}</span>
                    <span className={styles.userSubType}>{staff.userSubType}</span>
                  </div>
                  {staff.isAlreadyAdded && (
                    <span className={styles.alreadyAddedText}>Already Added</span>
                  )}
                </div>
              ))
            ) : staffSearchQuery && !searchingStaff ? (
              <div className={styles.noResults}>No staff found</div>
            ) : null}
          </div>
        </div>
      </div>
    );
  };
  
  return (
    <div className={styles.staffContainer}>
      <div className={styles['main-layout']}>
        {!isMobile && <LeftMenu />}
        <main className={styles['staff-main']}>
        <AuthCheckWrapper>
        {hasAccess ? (
            <>
          <div className={styles.pageHeader}>
            <h1 className={styles.pageTitle}>Staff Management</h1>
            <p className={styles.pageDescription}>
              Add and manage staff members for your organization.
            </p>
          </div>
          
          <div className={styles.staffActionsSection}>
            <div className={styles.searchContainer}>
              <button 
                className={styles.addStaffButton}
                onClick={() => setIsStaffModalVisible(true)}
              >
                <FontAwesomeIcon icon={faUserPlus} />
                <span>Add Staff</span>
              </button>
            </div>
          </div>
          
          <div className={styles.staffListContainer}>
            <h2 className={styles.sectionTitle}>Your Staff</h2>
            
            {displayedStaff.length === 0 ? (
              <div className={styles.emptyState}>
                <FontAwesomeIcon icon={faUsers} className={styles.emptyIcon} />
                <p>You haven't added any staff members yet</p>
              </div>
            ) : (
              <>
                <div className={styles.staffList}>
                  {displayedStaff.map((staff) => (
                    <div 
                      key={staff.id} 
                      className={styles.staffCard}
                    >
                      <div className={styles.staffCardContent}>
                        <div 
                          className={styles.staffInfo}
                          onClick={() => navigateToUserProfile(staff.userName)}
                          style={{ cursor: 'pointer' }}
                        >
                          <img
                            src={staff.profilePictureUrl || defaultUserImage}
                            alt={staff.fullName}
                            className={styles.staffImage}
                          />
                          <div className={styles.staffInfoText}>
                            <div className={styles.staffNameContainer}>
                              <span className={styles.staffName}>{staff.fullName}</span>
                              {staff.idVerificationStatus === 'Verified' && (
                                <img src={verifiedBadge} alt="Verified" className={styles.verifiedBadge} />
                              )}
                            </div>
                            <span className={styles.staffUsername}>@{staff.userName}</span>
                            <span className={styles.staffType}>{staff.userSubType}</span>
                            
                            {/* Display permission badges */}
                            <div className={styles.permissionsIndicator}>
                              {getPermissionBadges(staff.permissions)}
                            </div>
                          </div>
                        </div>
                        
                        <div className={styles.staffStatus}>
                          {staff.status === 'pending' && (
                            <span className={styles.pendingStatus}>Pending</span>
                          )}
                          {staff.status === 'accepted' && (
                            <span className={styles.acceptedStatus}>Active</span>
                          )}
                        </div>
                        
                        <div className={styles.staffActions}>
                          {/* Add edit button for permissions */}
                          <button
                            className={styles.editButton}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditStaff(staff);
                            }}
                            title="Edit staff permissions"
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </button>
                          
                          <button
                            className={styles.removeButton}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveStaff(staff.id);
                            }}
                            title="Remove staff member"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Load more button */}
                {hasMoreStaff && (
                  <button 
                    className={styles.loadMoreButton}
                    onClick={loadMoreStaff}
                    disabled={isLoadingMore}
                  >
                    {isLoadingMore ? 'Loading...' : 'Load More'}
                  </button>
                )}
              </>
            )}
          </div>
          
          {/* Modals */}
          {StaffSearchModal()}
          {EditPermissionsModal()}

          
          {/* Loading Overlay */}
          {isProcessing && (
            <div className={styles.loadingOverlay}>
              <div className={styles.loadingSpinner}></div>
              <div className={styles.loadingText}>
                Processing...
              </div>
            </div>
          )}
          </>
          ) : (
            <PermissionDenied />
          )}
          </AuthCheckWrapper>
        </main>
        {!isMobile && <RightMenu tours={tours} onTourSelect={handleTourSelect} />}
        
      </div>
    </div>
  );
};

export default Staff;